import { forgetStoredValue, getStoredValue, POST_LOGIN_PATH_KEY, storeValue } from '@sorrem-client/ui/shared/browser-storage';

const DEFAULT_LANDING_PATH = '/character/overview';

/**
 * Paths worth returning to after login: an invite, as a link to one crucible
 * (#216) or by its code. Anything else starts at Character Home.
 */
const RETURNABLE_PATHS: readonly RegExp[] = [
    /^\/character\/crucibles\/(?!create$|join$)[^/?#]+$/,
    /^\/character\/crucibles\/join\/[^/?#]+$/,
    /^\/join\/[^/?#]+$/,
];

function isReturnablePath(path: string): boolean {
    return RETURNABLE_PATHS.some((pattern) => pattern.test(path));
}

/**
 * Remembers where a signed-out player was headed (an invite), so login can
 * bring them back. Kept in this tab's session storage, which survives the
 * round trip to Discord.
 */
export function rememberPostLoginPath(path: string): void {
    if (!isReturnablePath(path)) {
        return;
    }
    // Without storage the player lands on Character Home, as before.
    storeValue('session', POST_LOGIN_PATH_KEY, path);
}

/**
 * The remembered path, if any. Looking doesn't forget it: the intro and
 * character creation can come between login and the invite, so it stays
 * until the player gets there (`forgetPostLoginPath`).
 */
export function peekPostLoginPath(): string | null {
    const path = getStoredValue('session', POST_LOGIN_PATH_KEY);
    return path !== undefined && isReturnablePath(path) ? path : null;
}

/** Forgets the remembered path, once the player has reached it (or logs out). */
export function forgetPostLoginPath(): void {
    forgetStoredValue('session', POST_LOGIN_PATH_KEY);
}

/** Where a player goes after login: the invite they were following, else Character Home. */
export function getPostLoginPath(): string {
    return peekPostLoginPath() ?? DEFAULT_LANDING_PATH;
}

/**
 * Where a new character goes once made: the invite the player was following,
 * including the one whose page they made it on (an account with no
 * characters is shown creation in place of any page), else Character Home.
 */
export function resolvePostCreationPath(currentPath: string): string {
    return peekPostLoginPath() ?? (isReturnablePath(currentPath) ? currentPath : DEFAULT_LANDING_PATH);
}
